import { AdminUserReport } from "@aika/types";
import { useEffect, useMemo, useState } from "react";
import { Box } from "../../components/Box";
import { Text } from "../../components/Text";
import { Panel } from "../../components/Panel";
import { Button } from "../../components/Button";
import axios from "axios";
import { format, differenceInDays } from "date-fns";
import SortableTable from "../../components/SortableTable";
import { Flex } from "components/Flex";

const subStatus = (x: AdminUserReport) =>
  x.isFounder ? (
    "Founder"
  ) : x.subscriptionId != null ? (
    <Text color="positive" fontSize="inherit">
      Subscribed
    </Text>
  ) : new Date() < new Date(x.freeTrialEnds) ? (
    `Free Trial (${differenceInDays(new Date(x.freeTrialEnds), new Date())
      .toString()
      .padStart(2, "0")} days)`
  ) : (
    "Expired"
  );

const emailStatus = (x: AdminUserReport) =>
  x.allowMarketingEmails
    ? `Marketing Emails (${
        x.emailConfirmed == null ? "Unconfirmed" : "Confirmed"
      })`
    : x.emailConfirmed == null
    ? "Unconfirmed"
    : "Confirmed";

const blocksPerDay = (x: AdminUserReport) =>
  x.blocksCreated /
  Math.max(1, differenceInDays(new Date(), new Date(x.signupDate)));

const AdminDashboard = () => {
  const [users, setUsers] = useState<AdminUserReport[]>([]);
  const [excludeExpired, setExcludeExpired] = useState<boolean>(false);

  useEffect(() => {
    axios.get("/api/admin/users").then(({ data }) => setUsers(data));
  }, []);

  const refresh = () => {
    axios.get("/api/admin/users").then(({ data }) => setUsers(data));
  };

  const columns = useMemo(
    () => [
      {
        name: "email",
        label: "Email",
      },
      {
        name: "emailStatus",
        label: "Email Status",
        format: emailStatus,
        sortFormat: emailStatus,
      },
      {
        name: "subStatus",
        label: "Sub Status",
        format: subStatus,
        sortFormat: subStatus,
      },
      {
        name: "founderSource",
        label: "Founder Source",
        format: (x) =>
          !x.isFounder
            ? "N/A"
            : x.founderSource == null
            ? "free"
            : x.founderSource,
        sortFormat: (x) =>
          !x.isFounder
            ? "N/A"
            : x.founderSource == null
            ? "free"
            : x.founderSource,
      },
      {
        name: "lastAccess",
        label: "Last Access",
        format: (x: AdminUserReport) =>
          x.lastAccess == null
            ? "Never"
            : format(new Date(x.lastAccess), "dd MMM yyyy HH:mm"),
        sortFormat: (x) => new Date(x.lastAccess),
      },
      {
        name: "signupDate",
        label: "Sign up",
        format: (x: AdminUserReport) =>
          format(new Date(x.signupDate), "dd MMMM yyyy"),
      },
      {
        name: "blocksCreated",
        label: "Blocks",
      },
      {
        name: "blocksPerDay",
        label: "Blocks/Day",
        format: (x: AdminUserReport) => blocksPerDay(x).toFixed(2),
        sortFormat: blocksPerDay,
      },
      {
        name: "source",
        label: "Source",
        format: (x: AdminUserReport) => x.source?.utm_source,
        sortFormat: (x: AdminUserReport) => x.source?.utm_source,
      },
      {
        name: "campaign",
        label: "Campaign",
        format: (x: AdminUserReport) => x.source?.utm_campaign,
        sortFormat: (x: AdminUserReport) => x.source?.utm_campaign,
      },
    ],
    []
  );

  return (
    <Box p={[2, 3]}>
      <Flex justifyContent="space-between" mb={2}>
        <Text fontSize={4} fontWeight={600}>
          Users
        </Text>
        <Flex>
          <Button onClick={refresh}>Refresh</Button>
          <Button ml={2} onClick={() => setExcludeExpired((old) => !old)}>
            {excludeExpired ? "Exclude" : "Include"} Expired
          </Button>
        </Flex>
      </Flex>
      <Panel p={0}>
        <SortableTable
          columns={columns}
          defaultSort={4}
          defaultDesc
          data={users.filter(
            (x) => !excludeExpired || subStatus(x) !== "Expired"
          )}
        />
      </Panel>
    </Box>
  );
};

export default AdminDashboard;
